import { memo, useCallback, useEffect, useState } from 'react'
import { Maximize2, MessageSquarePlus } from 'lucide-react'
import { renderMermaidDiagram } from '@/lib/mermaid'
import { openMermaidInChat } from '@/lib/mermaid-chat'
import { openMermaidLightbox } from '@/lib/mermaid-lightbox'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import './mermaid-diagram.css'

const ICON_SIZE = 14

type MermaidDiagramProps = {
  source: string
  className?: string
  documentId?: string | null
  documentTitle?: string | null
  documentPath?: string | null
  /** Show `{ }` toggle to view raw Mermaid source (default true in preview). */
  showSourceToggle?: boolean
}

export const MermaidDiagram = memo(function MermaidDiagram({
  source,
  className,
  documentId = null,
  documentTitle = null,
  documentPath = null,
  showSourceToggle = true,
}: MermaidDiagramProps) {
  const [svgHtml, setSvgHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showingSource, setShowingSource] = useState(false)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setSvgHtml(null)

    void renderMermaidDiagram(source).then((result) => {
      if (cancelled) return
      if (result.ok) {
        setSvgHtml(result.svg)
        setError(null)
      } else {
        setSvgHtml(null)
        setError(result.error)
      }
    })

    return () => {
      cancelled = true
    }
  }, [source])

  const handleExpand = useCallback(() => {
    if (!svgHtml) return
    openMermaidLightbox({ svgHtml, source })
  }, [svgHtml, source])

  const toggleSource = useCallback(() => {
    setShowingSource((v) => !v)
  }, [])

  const handleChat = useCallback(() => {
    openMermaidInChat({ source, error, documentId, documentTitle, documentPath })
  }, [source, error, documentId, documentTitle, documentPath])

  const diagramVisible = !showingSource && !!svgHtml && !error
  const sourceVisible = showingSource
  const errorVisible = !showingSource && !!error
  const chatLabel = error ? 'Fix in chat' : 'Add to chat'

  return (
    <div
      className={cn('mermaid-block not-prose', error && 'mermaid-block--has-error', className)}
      data-testid={testIds.mermaid.block}
    >
      <div className="mermaid-block-actions">
        <button
          type="button"
          className="mermaid-block-btn mermaid-chat-btn"
          title={chatLabel}
          aria-label={chatLabel}
          data-testid={testIds.mermaid.chatButton}
          onClick={handleChat}
        >
          <MessageSquarePlus size={ICON_SIZE} aria-hidden />
          <span className="mermaid-chat-btn-label">{chatLabel}</span>
        </button>

        {showSourceToggle ? (
          <button
            type="button"
            className={cn('mermaid-block-btn mermaid-toggle-btn', showingSource && 'active')}
            title="Toggle source"
            aria-label="Toggle Mermaid source"
            data-testid={testIds.mermaid.toggleSourceButton}
            onClick={toggleSource}
          >
            {'{ }'}
          </button>
        ) : null}

        {svgHtml ? (
          <button
            type="button"
            className="mermaid-block-btn mermaid-expand-btn"
            title="Expand diagram"
            aria-label="Expand diagram to fullscreen"
            data-testid={testIds.mermaid.expandButton}
            onClick={handleExpand}
          >
            <Maximize2 size={ICON_SIZE} aria-hidden />
          </button>
        ) : null}
      </div>

      <div
        className="mermaid-diagram"
        data-testid={testIds.mermaid.diagram}
        style={{ display: diagramVisible ? 'flex' : 'none' }}
        dangerouslySetInnerHTML={diagramVisible ? { __html: svgHtml! } : undefined}
      />

      {errorVisible ? (
        <div className="mermaid-error" data-testid={testIds.mermaid.error} role="status">
          <p className="mermaid-error-title">Diagram could not be rendered</p>
          <p className="mermaid-error-detail">{error}</p>
          <p className="mermaid-error-hint">
            Use <strong>Fix in chat</strong>
            {showSourceToggle ? <> or {'{ }'} to inspect the source</> : null}.
          </p>
        </div>
      ) : null}

      <pre
        className="mermaid-source"
        data-testid={testIds.mermaid.source}
        style={{ display: sourceVisible ? 'block' : 'none' }}
      >
        <code>{source}</code>
      </pre>

      {!diagramVisible && !errorVisible && !sourceVisible ? (
        <MermaidDiagramLoading />
      ) : null}
    </div>
  )
})

function MermaidDiagramLoading() {
  return (
    <div className="mermaid-diagram text-muted-foreground text-sm" aria-busy="true">
      Rendering diagram...
    </div>
  )
}
